import { useEffect, useRef } from 'react';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from './useXpSummary';

/**
 * Keeps the header XP summary live the same way the coin balance already is. Whenever
 * the economy store's coin total changes — a daily game finished, a daily reward was
 * claimed, a challenge settled — the XP ledger has almost certainly changed too, so we
 * refetch the summary. That one signal drives the header ring/level, the "+X XP" flash
 * (specs/xp-and-ranks) and the level/rank celebrations without threading xpAwarded
 * through every game/challenge endpoint. Mounted once via XpGainHost.
 */
export function useXpLiveSync(): void {
  const coins = useEconomyStore((s) => s.coins);
  const coinsLoaded = useEconomyStore((s) => s.coinsLoaded);
  const invalidateXp = useInvalidateXpSummary();
  const prevCoinsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!coinsLoaded) return;
    const prev = prevCoinsRef.current;
    prevCoinsRef.current = coins;
    // First trusted balance is the baseline; a no-op set changes nothing. Any real
    // move (earn or spend) triggers a refetch — spends simply return no XP delta.
    if (prev === null || prev === coins) return;
    invalidateXp();
  }, [coins, coinsLoaded, invalidateXp]);
}
